import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, MasteryItem, StreakInfo, Badge } from '../api';
import './Dashboard.css';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mastery, setMastery] = useState<MasteryItem[]>([]);
  const [streak, setStreak] = useState<StreakInfo | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);

  const startPractice = (conceptId: number) => {
    navigate(`/practice?conceptId=${conceptId}`);
  };

  useEffect(() => {
    // Load mastery (required) separately from gamification (optional)
    api.getMastery()
      .then(setMastery)
      .catch(console.error)
      .finally(() => setLoading(false));

    // Gamification features are optional - don't break if unavailable
    api.getStreak().then(setStreak).catch(() => {});
    api.getBadges().then(data => setBadges(data.badges.filter(b => b.earned))).catch(() => {});
  }, []);

  const masteredCount = mastery.filter(m => m.masteryLevel >= 0.8).length;

  return (
    <div className="dashboard">
      <header className="header">
        <h1>KillerHomework</h1>
        <div className="header-actions">
          {streak && streak.currentStreak > 0 && (
            <span className="streak-badge" title={`${streak.currentStreak} day streak!`}>
              🔥 {streak.currentStreak}
            </span>
          )}
          <span className="xp-badge">{user?.xpPoints || 0} XP</span>
          <span className="user-name">{user?.name}</span>
          <button onClick={logout} className="btn btn-secondary">Logout</button>
        </div>
      </header>

      <main className="dashboard-content">
        <section className="welcome-section">
          <h2>Welcome back, {user?.name?.split(' ')[0]}!</h2>
          <p>Ready to practice some math?</p>
          <Link to="/practice" className="btn btn-primary practice-btn">
            Start Practice Session
          </Link>
        </section>

        <section className="stats-section">
          <div className="stat-card">
            <div className="stat-value">{streak?.currentStreak || 0}</div>
            <div className="stat-label">🔥 Day Streak</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{masteredCount}</div>
            <div className="stat-label">Concepts Mastered</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{badges.length}</div>
            <div className="stat-label">🏆 Badges Earned</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{streak?.totalPracticeDays || 0}</div>
            <div className="stat-label">Total Practice Days</div>
          </div>
        </section>

        {badges.length > 0 && (
          <section className="badges-section">
            <h3>🏆 Your Badges</h3>
            <div className="badges-grid">
              {badges.slice(0, 6).map(badge => (
                <div key={badge.id} className="badge-card" title={badge.description}>
                  <div className="badge-icon">🎖️</div>
                  <div className="badge-title">{badge.title}</div>
                  <div className="badge-xp">+{badge.xpReward} XP</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mastery-section">
          <h3>Your Progress</h3>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="mastery-grid">
              {mastery.map(item => (
                <div
                  key={item.conceptId}
                  className="mastery-card clickable"
                  onClick={() => startPractice(item.conceptId)}
                  title="Click to practice this concept"
                >
                  <div className="mastery-header">
                    <span className="lesson-number">Lesson {item.lessonNumber}</span>
                    {item.dueBy && (
                      <span className="due-date">Due {new Date(item.dueBy).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</span>
                    )}
                    {item.verifiedByTest && <span className="verified-badge">Verified</span>}
                  </div>
                  <h4>{item.title}</h4>
                  <div className="progress-bar">
                    <div
                      className={`progress-bar-fill ${item.masteryLevel >= 0.8 ? 'high' : item.masteryLevel >= 0.4 ? 'medium' : 'low'}`}
                      style={{ width: `${item.masteryLevel * 100}%` }}
                    />
                  </div>
                  <div className="mastery-stats">
                    <span>{Math.round(item.masteryLevel * 100)}% Complete</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
